/* eslint-disable react-hooks/exhaustive-deps */
import { IGResponse } from "@common/models";
import { IMedia, IPage } from "@common/models/CMS";
import FaqItem from "@components/faq/FaqItem";
import Alert from "@components/general/Alert";
import Loading from "@components/general/Loading";
import RelativePagePanel from '@components/RelativePagePanel';
import TermsConditions from "@components/TermsConditionsItem";
import { SlideShowWidget } from "@components/Widgets/slideShowWidget/index";
import WidgetContainer from "@components/Widgets/WidgetContainer";
import { getFilePath } from "@config/appConfig";
import { AppAction, IAppAction } from "@misc/Action";
import { GET_PAGE, useApolloQuery } from "@service/graphql";
import Splash from "@service/Splash";
import Translate from "@service/Translate";
import { FC, Fragment, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

interface IPageProps {
    className?: string
    headerContent?: JSX.Element
    bodyContainer?: boolean;
    orderSetting?: {
        children: number;
        content: number;
    }
}

export const allowedExtentionsVideos = ["mp4"];

export const Page: FC<IPageProps> = ({ children, className, headerContent, orderSetting, bodyContainer = true }) => {
    const { tr } = Translate;
    const headerRef = useRef<HTMLImageElement>(null);
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { data, loading, error } = useApolloQuery<IGResponse<IPage[]>>(GET_PAGE, { variables: { code: pathname === "/" ? "home" : pathname?.substring(1) } });
    const page = data?.page.length ? data.page[0] : {};
    const pageSettings = page.settings?.contentItems.length ? page.settings?.contentItems[0] : {};

    useEffect(() => {
        if (page) {
            dispatch<IAppAction>({
                type: AppAction.headerStyle,
                payload: { headerStyle: { menuStyle: pageSettings?.menuStyle || "Solid", menuColor: pageSettings?.menuColor || "White" } }
            });
            dispatch<IAppAction>({
                type: AppAction.FooterMode,
                payload: { footerMode: pageSettings?.footerMode || "Full" }
            });
            if (!loading)
                setTimeout(() => Splash.hide(), 500);
        }
    }, [data]);

    useEffect(() => {
        document.addEventListener("scroll", handleScroll);

        return (() => {
            document.removeEventListener("scroll", handleScroll);
            dispatch<IAppAction>({ type: AppAction.headerStyle, payload: { headerStyle: { menuStyle: "Solid", menuColor: "Default" } } })
        })
    }, [])

    const handleScroll = () => {
        const scrollState = window.scrollY;
        if (headerRef.current)
            headerRef.current.style.backgroundPositionY = `${(scrollState - 80) * 0.5 > 0 ? (scrollState - 80) * 0.5 : 0}px`;
    };

    const relatedPagesPanels = ((page?.relatedPages?.contentItems?.length || 0) < 4 ? page?.relatedPages?.contentItems : page?.relatedPages?.contentItems?.slice(0, 2))
        ?.map((item, index) =>
            <div key={`related_page_panel_${index}`} className="related-pages col-1-3 col-md-1-2 col-sm-1-1">
                <RelativePagePanel
                    title={item.title || ""}
                    description={item.description || ""}
                    imageUrl={item.cover || {} as IMedia}
                    redirectUrl={`/${item.code?.code}` || ""}
                />
            </div>
        );

    const main = (
        <main className="row flex">
            <section className="col-1-1" style={{ order: orderSetting?.content }}>
                <WidgetContainer
                    generalContainer={page}
                />
            </section>
            <section className="col-1-1" style={{ order: orderSetting?.children }}>{children}</section>
        </main>
    );

    return (
        <main className={`page${className ? ` ${className}` : ""}`}>
            {
                error ? <div className="container"><Alert type="danger" variant="contain" title={tr("err_get_data")} /></div> :
                    loading && pathname !== "/" ? <Loading showLoader={loading} /> :
                        <Fragment>
                            {
                                pageSettings?.coverMode === "None" ? "" :
                                    < header
                                        ref={headerRef}
                                        className={`page-header${pageSettings?.menuStyle === 'Transparent' ? ' transparent-header' : ''}`}
                                        style={{
                                            height: pageSettings.coverMode === "Full" ? "100vh" : "",
                                            backgroundImage: page?.covers?.contentItems.length ? "" : `url(${getFilePath(page?.cover?.paths.find(x => !x.includes("video-poster")), true)})`
                                        }}
                                    >
                                        {
                                            page?.covers?.contentItems.length ?
                                                <SlideShowWidget
                                                    dataSource={{
                                                        animation: "SlideAndZoomOut",
                                                        autoDisplay: true,
                                                        backgroundMode: "Full",
                                                        progressBar: pathname === "/" ? "Point" : "Stack",
                                                        progressBarPointItemIsOver: true,
                                                        slides: { contentItems: page?.covers?.contentItems }
                                                    }}
                                                />
                                                :
                                                <div className="container header-container">
                                                    <article className="header-article">
                                                        <h1 className="header-title">{page?.title}</h1>
                                                        <p className="header-descrition">{page?.description}</p>
                                                    </article>
                                                    {headerContent}
                                                </div>
                                        }
                                    </header>
                            }
                            <main className="page-body">
                                {
                                    orderSetting ?
                                        bodyContainer ? <div className="container">{main}</div> : main
                                        :
                                        children ?
                                            bodyContainer ? <div className="container">{children}</div> : children
                                            :
                                            <WidgetContainer
                                                generalContainer={page}
                                            />
                                }
                            </main>
                            <footer className="container page-footer">
                                {
                                    page?.termsConditions?.contentItems.map((terms, index) => <TermsConditions key={`term_condition_item_${index}`} data={terms} />)}
                                {
                                    page?.fAQs?.contentItems.map((topic, index) => topic.items.contentItems?.length &&
                                        <section key={`faq_topic_item_${index}`} className="faq-topic">
                                            <h2 className="page-footer-header">{tr("faq_title")} {topic.displayText}</h2>
                                            <div className="faq-article row flex justify-center">
                                                {
                                                    page?.fAQs?.contentItems[index].items.contentItems.map((item, index) =>
                                                        <FaqItem
                                                            key={`faq_item_${index}`}
                                                            data={item}
                                                        />
                                                    )
                                                }
                                            </div>
                                        </section>
                                    )
                                }
                                {
                                    page?.relatedPages?.contentItems?.length ?
                                        <section className="page-related-page">
                                            <h2 className="page-related-page-header">{tr("page_related")}</h2>
                                            <div className="related-page-article row flex justify-center">
                                                {relatedPagesPanels}
                                                {
                                                    page.relatedPages.contentItems.length < 4 ? "" :
                                                        <section className="col-1-3 col-md-1-2 col-sm-1-1">
                                                            <div className="related-pages-urls">
                                                                {
                                                                    page.relatedPages.contentItems.map((item, index) =>
                                                                        <Link
                                                                            key={`related_page_link_${index}`}
                                                                            to={`/${item.code?.code}` || ""}
                                                                            className={(index === (page?.relatedPages?.contentItems?.length || 0) - 1) ? "url-container" : "url-container after"}
                                                                        >
                                                                            {item.title}
                                                                        </Link>
                                                                    )
                                                                }
                                                            </div>
                                                        </section>
                                                }
                                            </div>
                                        </section> : ''
                                }
                            </footer>
                        </Fragment>
            }
        </main >
    )
}